/**
 * Визард заказа СОУТ (canon service-sout). Шаги: Организация → Комиссия → Ответственный →
 * Рабочие места → Проверка, затем экран «Заказ отправлен». Прогресс-бар (Stepper), кнопки
 * Назад/Далее, автосохранение черновика (debounced PATCH), блокировка «Далее»/подачи при ошибках
 * шага с подсветкой и счётчиком, повторный прогон проверок перед подачей/выгрузкой (§3d).
 * Для submitted-заказа (anketa.canEdit=false) визард открывается read-only (просмотр анкеты).
 */
import { useEffect, useRef, useState } from 'react'
import { CheckCircle2, Trash2, Plus, Pencil } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  companyApi,
  ordersApi,
  soutApi,
  ApiError,
  type CommissionMember,
  type CompanyProfile,
  type Employee,
  type OrderDetail,
  type Responsible,
  type SoutAnketa,
  type SoutLine,
  type UpdateCompanyProfileInput,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import {
  commissionSummary,
  emptyCommissionMember,
  formatPhone,
  validateAnketa,
  validateCommission,
  validateResponsible,
  workplacesCountLabel,
} from '@/lib/sout'
import {
  SOUT_AUTOSAVE_HINT,
  SOUT_CHECKLIST,
  SOUT_COMMISSION_ODD_HINT,
  SOUT_IMPORT_DESCRIPTION,
  SOUT_IMPORT_TITLE,
  SOUT_REQUISITES_HINT,
  SOUT_WARNING,
} from './sout-copy'
import { useDebounced } from './use-debounced'
import { WorkplacesStep } from './workplaces-step'
import { OrderImportDialog } from '@/orders/order-import-dialog'
import { LabeledCheckbox } from './labeled-checkbox'
import { CompanyRequisitesStep } from '@/company/requisites-step'
import { AddToStructureModal } from '@/company/add-to-structure-modal'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { Alert } from '@/design-system/components/feedback/alert'
import { Stepper } from '@/design-system/components/data/stepper'
import {
  EmployeeSelect,
  type EmployeeSelectGroup,
  type EmployeeSelectOption,
} from '@/company/employee-select'

const STEPS = ['Организация', 'Комиссия', 'Ответственный', 'Рабочие места', 'Проверка']

/** СОУТ-скоупная обязательность реквизитов (IC-3, реш.4): ОКПО/ОКВЭД/ОКТМО. */
const SOUT_REQUIRED_REQUISITES = { okpo: true, okved: true, oktmo: true } as const

// --- Шаг: Комиссия ---

/** Какую строку комиссии заполнить созданным сотрудником (D3). */
export type CommissionTarget = { role: 'chair' } | { role: 'member'; index: number }

/** Куда автоселектить созданного сотрудника: строка комиссии (D3) или ответственный (MR !11). */
export type AddEmployeeTarget = CommissionTarget | { role: 'responsible' }

/** Снимок подписанта из карточки сотрудника: personId + замороженные ФИО/должность + роль (D2). */
function memberFromEmployee(emp: Employee, isChair: boolean): CommissionMember {
  // positionId в форме комиссии больше не выбирается отдельно (D3) — источник должности —
  // карточка сотрудника; в снимок уходит замороженный текст `position`.
  return { personId: emp.id, fullName: emp.fullName, position: emp.position, positionId: '', isChair }
}

function commissionParts(members: CommissionMember[]): {
  chair: CommissionMember | null
  regular: CommissionMember[]
} {
  return {
    chair: members.find((m) => m.isChair) ?? null,
    regular: members.filter((m) => !m.isChair),
  }
}

/** Пересборка плоского массива подписантов из председателя + членов (порядок: председатель первым). */
function assembleCommission(
  chair: CommissionMember | null,
  regular: CommissionMember[],
): CommissionMember[] {
  const arr: CommissionMember[] = []
  if (chair) arr.push({ ...chair, isChair: true })
  regular.forEach((m) => arr.push({ ...m, isChair: false }))
  return arr
}

/**
 * Поместить/очистить подписанта в целевой строке (используется и в редакторе, и при автоселекте
 * после «+ Добавить сотрудника»). `value == null` для строки-члена удаляет строку.
 */
function putCommissionMember(
  members: CommissionMember[],
  target: CommissionTarget,
  value: CommissionMember | null,
): CommissionMember[] {
  const { chair, regular } = commissionParts(members)
  if (target.role === 'chair') return assembleCommission(value, regular)
  const nextRegular = value
    ? regular.map((m, j) => (j === target.index ? value : m))
    : regular.filter((_, j) => j !== target.index)
  return assembleCommission(chair, nextRegular)
}

/**
 * Выбор сотрудника в строке комиссии: общий combobox с поиском по ФИО (centrika-docs, common.md,
 * MR !22 — «+ Добавить сотрудника» → разделительная черта → сотрудники; поиск только по ФИО,
 * служебная опция в фильтрации не участвует). Уже занятые в других строках исключаются.
 */
function EmployeePicker({
  employees,
  value,
  frozenLabel,
  excludeIds,
  ariaLabel,
  invalid,
  id,
  required,
  onSelect,
  onAddNew,
}: {
  employees: Employee[]
  value: string
  /** Замороженное ФИО из снимка — чтобы показать текущий выбор, даже если сотрудника нет в списке. */
  frozenLabel: string
  excludeIds: Set<string>
  ariaLabel: string
  invalid?: boolean
  id?: string
  /** Обязательность для вспомогательных технологий (маркер «*» у подписи от них скрыт). */
  required?: true | undefined
  onSelect: (emp: Employee) => void
  onAddNew: () => void
}) {
  const available = employees.filter((e) => e.id === value || !excludeIds.has(e.id))
  // Текущий выбор может отсутствовать в activeOnly-списке (деактивирован) — показываем его ФИО.
  const missingCurrent = Boolean(value) && !available.some((e) => e.id === value)
  // Замороженное ФИО может быть пустым (снимок с personId, но потерянным ФИО) — тогда ищем по
  // той же подписи, что показана, иначе строка текущего выбора пропадала бы при любом запросе.
  const frozen = frozenLabel || 'Выбранный сотрудник'
  const options: EmployeeSelectOption[] = [
    ...(missingCurrent ? [{ value, label: frozen, fullName: frozen }] : []),
    ...available.map((e) => ({ value: e.id, label: e.fullName, fullName: e.fullName })),
  ]
  return (
    <EmployeeSelect
      value={value}
      options={options}
      aria-label={ariaLabel}
      id={id}
      aria-required={required}
      invalid={invalid}
      onSelect={(v) => {
        const emp = employees.find((e) => e.id === v)
        if (emp) onSelect(emp)
      }}
      onAddNew={onAddNew}
    />
  )
}

/** Одна строка подписанта: ФИО (выбор сотрудника) + Должность (read-only из карточки). */
function SignerRow({
  member,
  employees,
  usedIds,
  readOnly,
  fioLabel,
  required = false,
  onSelect,
  onAddNew,
  onRemove,
}: {
  member: CommissionMember | null
  employees: Employee[]
  usedIds: Set<string>
  readOnly: boolean
  fioLabel: string
  /** Показывать маркер обязательности у ФИО (MR #66: только у председателя). */
  required?: boolean
  onSelect: (emp: Employee) => void
  onAddNew: () => void
  onRemove?: () => void
}) {
  const personId = (member?.personId ?? '').trim()
  const frozenName = member?.fullName ?? ''
  // Back-compat (D6): в строке есть замороженное ФИО, но нет personId → требуется перевыбор.
  const needsReselect = !readOnly && Boolean(frozenName) && !personId
  const exclude = new Set(usedIds)
  if (personId) exclude.delete(personId)

  return (
    <div className="flex items-start gap-2">
      <div className="grid flex-1 grid-cols-1 gap-3 nw:grid-cols-2">
        <Field
          label={fioLabel}
          // Маркер обязательности показываем только у председателя (MR #66); члены комиссии
          // необязательны. Выбор сотрудника в уже добавленной строке члена всё равно проверяется
          // (`commissionSummary`), но без звёздочки у подписи. В просмотре заполнять нечего.
          required={required && !readOnly}
          className="my-0"
          error={needsReselect ? `Ранее: ${frozenName} — выберите сотрудника заново` : undefined}
        >
          {(props) =>
            readOnly ? (
              <Input {...props} value={frozenName} disabled />
            ) : (
              <EmployeePicker
                employees={employees}
                value={personId}
                frozenLabel={frozenName}
                excludeIds={exclude}
                ariaLabel={fioLabel}
                id={props.id}
                required={props['aria-required']}
                invalid={needsReselect}
                onSelect={onSelect}
                onAddNew={onAddNew}
              />
            )
          }
        </Field>
        <Field label="Должность" className="my-0">
          {(props) => <Input {...props} value={member?.position ?? ''} disabled />}
        </Field>
      </div>
      {onRemove && !readOnly ? (
        <Button
          size="sm"
          variant="secondary"
          className="mt-6 shrink-0"
          onClick={onRemove}
          aria-label="Удалить члена комиссии"
          leftIcon={<Trash2 className="size-4 stroke-current" aria-hidden />}
        >
          <span className="sr-only nw:not-sr-only">Удалить</span>
        </Button>
      ) : null}
    </div>
  )
}

function CommissionEditor({
  members,
  employees,
  readOnly,
  onChange,
  onAddEmployee,
}: {
  members: CommissionMember[]
  employees: Employee[]
  readOnly: boolean
  onChange: (next: CommissionMember[]) => void
  onAddEmployee: (target: CommissionTarget) => void
}) {
  const { chair, regular } = commissionParts(members)
  const usedIds = new Set(members.map((m) => (m.personId ?? '').trim()).filter(Boolean))
  const summary = commissionSummary(members)

  function selectChair(emp: Employee) {
    onChange(putCommissionMember(members, { role: 'chair' }, memberFromEmployee(emp, true)))
  }
  function selectMember(index: number, emp: Employee) {
    onChange(putCommissionMember(members, { role: 'member', index }, memberFromEmployee(emp, false)))
  }
  function addMember() {
    onChange(assembleCommission(chair, [...regular, emptyCommissionMember(false)]))
  }
  function removeMember(index: number) {
    onChange(putCommissionMember(members, { role: 'member', index }, null))
  }

  return (
    <div className="flex flex-col gap-4">
      <Card header="Председатель комиссии">
        <SignerRow
          member={chair}
          employees={employees}
          usedIds={usedIds}
          readOnly={readOnly}
          fioLabel="ФИО"
          required
          onSelect={selectChair}
          onAddNew={() => onAddEmployee({ role: 'chair' })}
        />
        <p className="mt-2 text-13 text-txt-tertiary">
          Обычно комиссию возглавляет руководитель или его заместитель.
        </p>
      </Card>

      <Card header="Члены комиссии">
        {/* sout-commission-odd: правило видно заранее, а не только по ошибке под списком. */}
        <p className="mb-3 text-13 text-txt-secondary">{SOUT_COMMISSION_ODD_HINT}</p>
        {regular.length === 0 ? (
          <p className="py-2 text-txt-secondary">Члены комиссии не добавлены.</p>
        ) : (
          <div className="flex flex-col gap-4">
            {regular.map((m, i) => (
              <SignerRow
                key={i}
                member={m}
                employees={employees}
                usedIds={usedIds}
                readOnly={readOnly}
                fioLabel="ФИО члена комиссии"
                onSelect={(emp) => selectMember(i, emp)}
                onAddNew={() => onAddEmployee({ role: 'member', index: i })}
                onRemove={() => removeMember(i)}
              />
            ))}
          </div>
        )}
        {!readOnly ? (
          <Button
            variant="secondary"
            size="sm"
            className="mt-4"
            onClick={addMember}
            leftIcon={<Plus className="size-4 stroke-current" aria-hidden />}
          >
            Добавить члена комиссии
          </Button>
        ) : null}
        {!readOnly && summary.message ? (
          <p className="mt-4 text-13 text-red" role="status">
            {summary.message}
          </p>
        ) : null}
      </Card>
    </div>
  )
}

// --- Шаг: Ответственный ---

/** Снимок ответственного из карточки сотрудника — как для строки комиссии (personId + ФИО +
 *  должность). Используется и при выборе из списка, и при автоселекте созданного сотрудника. */
type ResponsiblePick = { personId: string; fullName: string; position: string; positionId: string }

/** Кандидат в ответственные: должность + ФИО (метка), personId и снимок для выбора. */
type ResponsibleCandidate = { personId: string; label: string; pick: ResponsiblePick }

function candidateLabel(position: string, fullName: string): string {
  return position ? `${position} — ${fullName}` : fullName
}

/**
 * Кандидаты в ответственные (centrika-docs, `component.create-sout-order.responsible`, MR !11):
 * члены комиссии + остальные сотрудники организации (ответственным может быть любой сотрудник, не
 * только член комиссии). Возвращаются двумя группами для разделителей; сотрудник, уже входящий в
 * комиссию, во вторую группу не попадает (дедуп по `personId`).
 */
function responsibleCandidates(
  commission: CommissionMember[],
  employees: Employee[],
): { commission: ResponsibleCandidate[]; others: ResponsibleCandidate[] } {
  const commissionMembers = commission.filter((m) => (m.personId ?? '').trim())
  const commissionIds = new Set(commissionMembers.map((m) => (m.personId ?? '').trim()))
  const fromCommission = commissionMembers.map((m) => {
    const personId = (m.personId ?? '').trim()
    return {
      personId,
      label: candidateLabel(m.position, m.fullName),
      pick: { personId, fullName: m.fullName, position: m.position, positionId: m.positionId ?? '' },
    }
  })
  const others = employees
    .filter((e) => !commissionIds.has(e.id))
    .map((e) => ({
      personId: e.id,
      label: candidateLabel(e.position, e.fullName),
      pick: { personId: e.id, fullName: e.fullName, position: e.position, positionId: '' },
    }))
  return { commission: fromCommission, others }
}

/**
 * Выбор ответственного: «+ Добавить сотрудника» (акцент) → разделительная черта → члены комиссии
 * → разделительная черта → остальные сотрудники организации. Группировка «комиссия / остальные»
 * и разделитель между группами — требование страницы (MR !11); порядок служебных опций и поиск
 * по ФИО — общее требование (centrika-docs, common.md, MR !22).
 */
function ResponsiblePicker({
  commission,
  employees,
  value,
  frozenLabel,
  invalid,
  id,
  required,
  onSelect,
  onAddNew,
}: {
  commission: CommissionMember[]
  employees: Employee[]
  value: string
  /** Замороженное ФИО ответственного — чтобы показать текущий выбор, даже если его нет в списке. */
  frozenLabel: string
  invalid?: boolean
  id?: string
  /** Обязательность для вспомогательных технологий (маркер «*» у подписи от них скрыт). */
  required?: true | undefined
  onSelect: (pick: ResponsiblePick) => void
  onAddNew: () => void
}) {
  const candidates = responsibleCandidates(commission, employees)
  const all = [...candidates.commission, ...candidates.others]
  const missingCurrent = Boolean(value) && !all.some((c) => c.personId === value)
  const toOptions = (list: ResponsibleCandidate[]): EmployeeSelectOption[] =>
    list.map((c) => ({ value: c.personId, label: c.label, fullName: c.pick.fullName }))
  // Текущий выбор вне списка (деактивированный сотрудник) — отдельной первой группой: общее
  // требование запрещает вклинивать его между «+ Добавить сотрудника» и разделительной чертой.
  // Пустое замороженное ФИО (снимок без ФИО) ищется по показанной подписи — см. `EmployeePicker`.
  const frozen = frozenLabel || 'Выбранный сотрудник'
  const groups: EmployeeSelectGroup[] = [
    ...(missingCurrent
      ? [{ key: 'current', options: [{ value, label: frozen, fullName: frozen }] }]
      : []),
    { key: 'commission', options: toOptions(candidates.commission) },
    { key: 'others', options: toOptions(candidates.others) },
  ]
  return (
    <EmployeeSelect
      value={value}
      groups={groups}
      aria-label="Кто будет ответственным"
      placeholder="Выберите ответственного"
      id={id}
      aria-required={required}
      invalid={invalid}
      onSelect={(v) => {
        const found = all.find((c) => c.personId === v)
        if (found) onSelect(found.pick)
      }}
      onAddNew={onAddNew}
    />
  )
}

function ResponsibleEditor({
  responsible,
  commission,
  employees,
  readOnly,
  onChange,
  onAddEmployee,
}: {
  responsible: Responsible
  commission: CommissionMember[]
  employees: Employee[]
  readOnly: boolean
  onChange: (next: Responsible) => void
  /** Открыть диалог создания сотрудника «для ответственного» (MR !11). */
  onAddEmployee: () => void
}) {
  const errors = readOnly ? {} : validateResponsible(responsible)
  const show = (field: 'phone' | 'email') => (responsible[field].trim().length > 0 ? errors[field] : undefined)
  function update(patch: Partial<Responsible>) {
    onChange({ ...responsible, ...patch })
  }

  const personId = (responsible.personId ?? '').trim()
  const frozenName = responsible.fullName ?? ''
  // Back-compat (D6): старый черновик без personId — перевыбрать. «Выбыл из комиссии» больше НЕ
  // особый случай (MR !11): ответственным может быть любой сотрудник, а не только член комиссии.
  const needsReselect = !readOnly && Boolean(frozenName) && !personId

  // Выбор ответственного: ФИО/должность — замороженный снимок карточки сотрудника, read-only на
  // этом шаге. Телефон/email — префилл из карточки ВЫБРАННОГО сотрудника; заменяют предыдущие
  // значения только при выборе ДРУГОГО человека. Перевыбор ТОГО ЖЕ personId — no-op для phone/email:
  // не затираем уже введённые вручную правки устаревшим mount-time значением карточки сотрудника.
  function selectCandidate(pick: ResponsiblePick) {
    const samePerson = personId !== '' && pick.personId === personId
    if (samePerson) {
      update({ personId: pick.personId, fullName: pick.fullName, position: pick.position, positionId: pick.positionId })
      return
    }
    const emp = employees.find((e) => e.id === pick.personId)
    update({
      personId: pick.personId,
      fullName: pick.fullName,
      position: pick.position,
      positionId: pick.positionId,
      phone: emp?.phone ? formatPhone(emp.phone) : '',
      email: emp?.email ?? '',
    })
  }

  return (
    <Card header="Ответственный">
      {!readOnly ? (
        <div className="grid grid-cols-1 gap-3 nw:grid-cols-3">
          <Field
            label="Кто будет ответственным"
            required
            className="my-0"
            error={needsReselect ? `Ранее: ${frozenName} — выберите сотрудника заново` : undefined}
          >
            {(props) => (
              <ResponsiblePicker
                commission={commission}
                employees={employees}
                value={personId}
                frozenLabel={frozenName}
                id={props.id}
                required={props['aria-required']}
                invalid={needsReselect}
                onSelect={selectCandidate}
                onAddNew={onAddEmployee}
              />
            )}
          </Field>
        </div>
      ) : null}
      <div className="grid grid-cols-1 gap-3 nw:grid-cols-2">
        <Field label="ФИО" className="my-0">
          {(props) => <Input {...props} value={responsible.fullName} disabled />}
        </Field>
        <Field label="Должность" className="my-0">
          {(props) => <Input {...props} value={responsible.position} disabled />}
        </Field>
      </div>
      <div className="grid grid-cols-1 gap-3 nw:grid-cols-2">
        <Field
          label="Мобильный телефон"
          required={!readOnly}
          hint="+7 и 10 цифр"
          error={show('phone')}
          className="my-0"
        >
          {(props) => (
            <Input
              {...props}
              // Живая маска телефона (IC-3, п.5; formatPhone из прототипа sout.html) —
              // хранится уже отформатированное значение, серверная/клиентская проверки
              // телефона игнорируют пробелы/скобки/дефисы.
              value={formatPhone(responsible.phone)}
              invalid={Boolean(show('phone'))}
              disabled={readOnly}
              onChange={(e) => update({ phone: formatPhone(e.target.value) })}
              inputMode="tel"
              placeholder="+7 (___) ___-__-__"
            />
          )}
        </Field>
        <Field
          label="Электронная почта"
          required={!readOnly}
          error={show('email')}
          className="my-0"
        >
          {(props) => (
            <Input
              {...props}
              value={responsible.email}
              invalid={Boolean(show('email'))}
              disabled={readOnly}
              onChange={(e) => update({ email: e.target.value })}
              placeholder="ot@company.ru"
            />
          )}
        </Field>
      </div>
      <p className="mt-4 text-13 text-txt-tertiary">
        С этим человеком эксперт согласует график замеров; на почту придут результаты СОУТ.
      </p>
    </Card>
  )
}

// --- Визард ---

export interface SoutWizardProps {
  order: OrderDetail
  anketa: SoutAnketa
  /** Шаг, на который нужно открыть визард сразу при монтировании (напр. возврат со страницы
   *  рабочего места — `?step=3`, p3-sout-workplaces §4). Вне диапазона/не задан → шаг 0. */
  initialStep?: number
  /** id строки, к которой список «Рабочие места» должен прокрутиться при возврате (§3.1/§4). */
  anchorLineId?: string
  /** Вызывается после успешной отправки заказа (напр. перечитать контекст кабинета — centrika-docs!98). */
  onSubmitted?: () => void
}

export function SoutWizard({ order, anketa, initialStep, anchorLineId, onSubmitted }: SoutWizardProps) {
  const navigate = useNavigate()
  const [, setSearchParams] = useSearchParams()
  const readOnly = !anketa.canEdit

  const [step, setStep] = useState(
    initialStep !== undefined && initialStep >= 0 && initialStep < STEPS.length ? initialStep : 0,
  )

  // Держим текущий шаг в query (`?step=N`), ЗАМЕНЯЯ (не пуская) запись истории (p3-sout-workplaces
  // §4, Opus-ревью Волны 4, MAJOR-2): без этого браузерный «Назад» со страницы рабочего места
  // возвращает на `/orders/:id/sout` БЕЗ query — мастер открывался бы с шага 0 вместо «Рабочие
  // места». Обновление через функциональную форму `setSearchParams` СОХРАНЯЕТ прочие параметры
  // (в частности `line=` — якорь строки, который расставляет/убирает сама WorkplacesStep) —
  // перезапись значения именно того же `step` идемпотентна и не роняет якорь при монтировании.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('step', String(step))
        return next
      },
      { replace: true },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])
  // Счётчик принудительного открытия формы реквизитов (ремонтирует шаг 0 с
  // initialEditing=true) — используется переходом «Изменить» у секции «Организация»
  // на шаге «Проверка» (IC-3, п.6), чтобы не заставлять пользователя кликать дважды.
  const [orgEditRequest, setOrgEditRequest] = useState(0)
  const [organization, setOrganization] = useState<CompanyProfile>(anketa.organization)
  const [commission, setCommission] = useState<CommissionMember[]>(anketa.commission)
  const [responsible, setResponsible] = useState<Responsible>(anketa.responsible)
  const [pdConsent, setPdConsent] = useState<boolean>(anketa.pdConsent)
  const [lines, setLines] = useState<SoutLine[]>(anketa.lines)
  const [employees, setEmployees] = useState<Employee[]>([])
  // Откуда открыта модалка «+ Добавить сотрудника»: строка комиссии (D3) или ответственный (MR !11) —
  // по успеху созданный сотрудник автоселектится туда.
  const [addTarget, setAddTarget] = useState<AddEmployeeTarget | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<string[]>([])
  const [submitted, setSubmitted] = useState(false)
  // Загрузка «Анкеты и формы СОУТ» (order-import-legacy-forms): диалог и итог последней загрузки.
  const [importOpen, setImportOpen] = useState(false)
  const [importNotice, setImportNotice] = useState<string | null>(null)

  const anketaSave = useDebounced()
  // Автосохранение строки («Рабочие места») теперь живёт на отдельной странице карточки места
  // (p3-sout-workplaces D1, `pages/order-sout-workplace.tsx`) — свой дебаунс, flush перед
  // возвратом на список. Здесь строки уже не редактируются — `lines` только читается/заменяется
  // целиком после copy/remove и перечитывается заново при каждом монтировании визарда.

  // Актуальные значения анкеты для дебаунс-сейва (PATCH шлёт полный снимок — порядок схлопываний неважен).
  const stateRef = useRef({ commission, responsible, pdConsent })
  useEffect(() => {
    stateRef.current = { commission, responsible, pdConsent }
  }, [commission, responsible, pdConsent])

  useEffect(() => {
    if (readOnly) return
    const controller = new AbortController()
    // activeOnly (design D6): пикер для НОВОГО заказа не предлагает деактивированных.
    companyApi
      .listEmployees({ limit: 200, activeOnly: true }, controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setEmployees(res.items)
      })
      .catch(() => undefined)
    return () => controller.abort()
  }, [readOnly])

  async function runSave(fn: () => Promise<unknown>) {
    setSaving(true)
    setSaveError(null)
    try {
      await fn()
    } catch (err) {
      setSaveError(getErrorMessage(err, 'Не удалось сохранить черновик'))
    } finally {
      setSaving(false)
    }
  }

  function scheduleAnketaSave() {
    if (readOnly) return
    anketaSave.schedule(() =>
      runSave(() => soutApi.patchAnketa(order.id, { ...stateRef.current })),
    )
  }

  function changeCommission(next: CommissionMember[]) {
    setCommission(next)
    stateRef.current = { ...stateRef.current, commission: next }
    scheduleAnketaSave()
  }
  // Успех «+ Добавить сотрудника»: домешиваем созданного сотрудника в пикер (без повторного
  // listEmployees) и автоселектим его в источник открытия — строку комиссии (D3) или ответственного
  // (MR !11); модалку закрывает сама AddToStructureModal.
  function onEmployeeCreated(emp: Employee, target: AddEmployeeTarget) {
    setEmployees((prev) => (prev.some((e) => e.id === emp.id) ? prev : [emp, ...prev]))
    if (target.role === 'responsible') {
      changeResponsible({
        ...stateRef.current.responsible,
        personId: emp.id,
        fullName: emp.fullName,
        position: emp.position,
        positionId: '',
        phone: emp.phone ? formatPhone(emp.phone) : '',
        email: emp.email ?? '',
      })
      return
    }
    const next = putCommissionMember(
      stateRef.current.commission,
      target,
      memberFromEmployee(emp, target.role === 'chair'),
    )
    changeCommission(next)
  }
  function changeResponsible(next: Responsible) {
    setResponsible(next)
    stateRef.current = { ...stateRef.current, responsible: next }
    scheduleAnketaSave()
  }
  function changePdConsent(next: boolean) {
    setPdConsent(next)
    stateRef.current = { ...stateRef.current, pdConsent: next }
    scheduleAnketaSave()
  }

  // Шаг «Организация» (D1, §7.2, A1): правка реквизитов на первом шаге — общий паттерн
  // заказов обучения/профрисков. PATCH /orders/{id}/requisites применяет правку и к
  // профилю компании, и к снимку ЭТОГО заказа без модерации; свежий снимок реквизитов
  // (уже применённый к анкете) забираем повторным GET анкеты — это же значение
  // возвращаем в CompanyRequisitesStep (её форме) для локального обновления.
  async function saveRequisites(input: UpdateCompanyProfileInput): Promise<CompanyProfile> {
    await ordersApi.patchRequisites(order.id, input)
    const fresh = await soutApi.getAnketa(order.id)
    setOrganization(fresh.organization)
    return fresh.organization
  }

  // Загрузка из файла пишет на сервере и рабочие места, и анкету (комиссию, ответственного).
  // Перед ней досылаем отложенный автосейв анкеты — иначе он долетел бы ПОСЛЕ загрузки и затёр
  // загруженное прежним состоянием мастера; после — перечитываем анкету целиком.
  async function importFile(file: File) {
    await anketaSave.flush()
    return soutApi.importFile(order.id, file)
  }
  async function onImported(added: number) {
    setImportNotice(`Из файла добавлено: ${workplacesCountLabel(added)}.`)
    try {
      const fresh = await soutApi.getAnketa(order.id)
      setCommission(fresh.commission)
      setResponsible(fresh.responsible)
      setPdConsent(fresh.pdConsent)
      setLines(fresh.lines)
      stateRef.current = {
        commission: fresh.commission,
        responsible: fresh.responsible,
        pdConsent: fresh.pdConsent,
      }
    } catch (err) {
      setSaveError(getErrorMessage(err, 'Не удалось обновить анкету — обновите страницу'))
    }
  }

  // --- Валидность шагов (блокировка «Далее») ---
  const commissionError = validateCommission(commission)
  const responsibleErrors = validateResponsible(responsible)
  const validation = validateAnketa({ commission, responsible, pdConsent, lines })

  const stepValid: boolean[] = [
    true,
    commissionError === null,
    Object.keys(responsibleErrors).length === 0,
    validation.hasLines && validation.invalidLineCount === 0,
    validation.ok,
  ]

  function next() {
    if (step < STEPS.length - 1 && (readOnly || stepValid[step])) setStep(step + 1)
  }
  function back() {
    if (step > 0) setStep(step - 1)
  }
  // Переход к конкретному шагу с шага «Проверка» по кнопке «Изменить» (IC-3, п.6).
  function goTo(target: number) {
    if (target >= 0 && target < STEPS.length) setStep(target)
  }

  async function submit() {
    setSubmitError(null)
    setFieldErrors([])
    const v = validateAnketa({ commission, responsible, pdConsent, lines })
    if (!v.ok) {
      setSubmitError('Проверьте заполнение: есть незаполненные обязательные поля или нет согласия на ПД.')
      return
    }
    setSubmitting(true)
    try {
      // Дожидаемся долёта последнего отложенного PATCH анкеты, затем подаём: иначе
      // ordersApi.submit может уйти раньше последнего patchAnketa → ложный 422. Автосохранение
      // строк места живёт на отдельной странице (см. выше) и уже долетело до неё же навигацией.
      await anketaSave.flush()
      await ordersApi.submit(order.id)
      setSubmitted(true)
      onSubmitted?.()
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        setSubmitError('Сервер отклонил подачу: проверьте поля.')
        setFieldErrors((err.details ?? []).map((d) => `${d.field}: ${d.message}`))
      } else {
        setSubmitError(getErrorMessage(err, 'Не удалось отправить заказ'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <Card>
        <div className="flex flex-col items-center px-5 py-16 text-center">
          <CheckCircle2 className="mb-4 size-12 stroke-current text-green" aria-hidden />
          <h2 className="text-20 font-semibold text-txt">Заказ отправлен</h2>
          <p className="mt-2 max-w-narrow text-txt-secondary">
            Анкета СОУТ принята на проверку. Менеджер Центрики свяжется с вами; статус и ход работ
            видны в карточке заказа.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2.5">
            <Button onClick={() => navigate(`/orders/${order.id}`)}>Открыть заказ</Button>
            <Button variant="secondary" onClick={() => navigate('/sout')}>
              К дашборду «Спецоценка»
            </Button>
          </div>
        </div>
      </Card>
    )
  }

  return (
    <div>
      {/* Должность директора (шаг «Организация») — строгий выбор из справочника (PositionSelect,
          p2-position-dictionary Волна 3). Должность комиссии/ответственного (p3-sout-commission,
          волны 4/5) больше не выбирается отдельно — замороженный текст из карточки сотрудника
          (read-only), свободный ввод/datalist удалены. */}
      <Stepper steps={STEPS} current={step} className="mb-3" />
      <div className="mb-4 flex items-center justify-between gap-3 text-12 text-txt-tertiary">
        <span>{SOUT_AUTOSAVE_HINT}</span>
        <span aria-live="polite">{saving ? 'Сохранение…' : saveError ? '' : 'Черновик сохранён'}</span>
      </div>

      {saveError ? (
        <Alert variant="danger" className="mb-4">
          {saveError}
        </Alert>
      ) : null}
      {readOnly ? (
        <Alert variant="info" title="Только просмотр" className="mb-4">
          Заказ уже отправлен — анкета доступна только для чтения.
        </Alert>
      ) : null}

      {/* Шаг 0 — Организация (D1, §7.2, A1): редактируется клиентом (и менеджером) по
          общему паттерну заказов обучения/профрисков — не отдельная модель для СОУТ. */}
      {step === 0 ? (
        <div className="flex flex-col gap-4">
          {!readOnly ? (
            <Alert variant="info" title="Что подготовить">
              <ul className="ml-4 list-disc">
                {SOUT_CHECKLIST.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </Alert>
          ) : null}
          {!readOnly ? <Alert variant="info">{SOUT_REQUISITES_HINT}</Alert> : null}
          {/* Read-only компакт + «Изменить» (F2-11/FX-E) — общий паттерн заказов обучения/
              профрисков; `key` меняется при переходе «Изменить» из шага «Проверка», чтобы
              форма открылась сразу, не заставляя кликать дважды. */}
          <CompanyRequisitesStep
            key={orgEditRequest}
            organization={organization}
            readOnly={readOnly}
            readOnlyHint="Реквизиты зафиксированы в снимке заказа на момент подачи."
            onSave={saveRequisites}
            initialEditing={orgEditRequest > 0}
            hideManagerFields
            requireExtraRequisites={SOUT_REQUIRED_REQUISITES}
            onDaDataLookup={companyApi.dadataLookup}
          />
        </div>
      ) : null}

      {/* Шаг 1 — Комиссия */}
      {step === 1 ? (
        <CommissionEditor
          members={commission}
          employees={employees}
          readOnly={readOnly}
          onChange={changeCommission}
          onAddEmployee={setAddTarget}
        />
      ) : null}

      {/* Шаг 2 — Ответственный */}
      {step === 2 ? (
        <ResponsibleEditor
          responsible={responsible}
          commission={commission}
          employees={employees}
          readOnly={readOnly}
          onChange={changeResponsible}
          onAddEmployee={() => setAddTarget({ role: 'responsible' })}
        />
      ) : null}

      {/* Шаг 3 — Рабочие места */}
      {step === 3 && importNotice ? (
        <Alert variant="info" className="mb-4">
          {importNotice} Проверьте места с отметкой «дозаполнить» и разнесите оборудование,
          материалы и служебный транспорт в карточках мест.
        </Alert>
      ) : null}
      {importOpen ? (
        <OrderImportDialog
          title={SOUT_IMPORT_TITLE}
          description={SOUT_IMPORT_DESCRIPTION}
          preview={(file) => soutApi.importPreview(order.id, file)}
          importFile={(file) => importFile(file)}
          onImported={(result) => void onImported(result.added)}
          onClose={() => setImportOpen(false)}
        />
      ) : null}
      {step === 3 ? (
        <WorkplacesStep
          orderId={order.id}
          lines={lines}
          readOnly={readOnly}
          onLinesChange={setLines}
          anchorLineId={anchorLineId}
          onImportFile={readOnly ? undefined : () => setImportOpen(true)}
        />
      ) : null}

      {/* Шаг 4 — Проверка: полное эхо ключевых данных + переход к шагу по «Изменить»
          (IC-3, п.6; образец renderStep5 прототипа sout.html). */}
      {step === 4 ? (
        <div className="flex flex-col gap-4">
          <Alert variant="info" title="Внимание">
            {SOUT_WARNING}
          </Alert>

          <ReviewSection
            title="Организация"
            onEdit={
              readOnly
                ? undefined
                : () => {
                    goTo(0)
                    // Открыть форму сразу, а не компакт-вид — пользователь уже выразил
                    // намерение отредактировать реквизиты кнопкой «Изменить».
                    setOrgEditRequest((n) => n + 1)
                  }
            }
          >
            <ReviewTable
              rows={[
                ['Полное наименование', organization.nameFull],
                ['ИНН', organization.inn],
                ['КПП', organization.kpp],
                ['ОГРН', organization.ogrn],
                ['ОКПО', organization.okpo],
                ['ОКОГУ', organization.okogu ?? ''],
                ['ОКВЭД', organization.okved],
                ['ОКТМО', organization.oktmo],
                ['Юридический адрес', organization.legalAddress],
                [
                  'Руководитель',
                  [organization.directorPosition, organization.directorName]
                    .filter((s) => s.trim())
                    .join(' — '),
                ],
                ['Официальный e-mail', organization.email],
              ]}
            />
          </ReviewSection>

          <ReviewSection
            title="Комиссия"
            status={commissionError === null ? 'ok' : 'warn'}
            statusText={commissionError ?? undefined}
            onEdit={readOnly ? undefined : () => goTo(1)}
          >
            {commission.length === 0 ? (
              <p className="text-txt-secondary">Члены комиссии не добавлены.</p>
            ) : (
              <ReviewTable
                rows={[
                  ...commission.map((m, i): [string, string] => [
                    m.isChair ? 'Председатель' : `Член №${i + 1}`,
                    [m.fullName, m.position].filter((s) => s.trim()).join(' — '),
                  ]),
                  ['Всего', `${commission.length} чел.`],
                ]}
              />
            )}
          </ReviewSection>

          <ReviewSection
            title="Ответственный"
            status={Object.keys(responsibleErrors).length === 0 ? 'ok' : 'warn'}
            statusText={
              Object.keys(responsibleErrors).length === 0
                ? undefined
                : responsibleErrors.personId
                  ? 'Выберите ответственного'
                  : responsibleErrors.fullName
                    ? 'Проверьте данные ответственного'
                    : 'Заполните телефон и email'
            }
            onEdit={readOnly ? undefined : () => goTo(2)}
          >
            <ReviewTable
              rows={[
                ['ФИО', responsible.fullName],
                ['Должность', responsible.position],
                ['Телефон', formatPhone(responsible.phone)],
                ['Email', responsible.email],
              ]}
            />
          </ReviewSection>

          <ReviewSection
            title="Рабочие места"
            status={validation.hasLines && validation.invalidLineCount === 0 ? 'ok' : 'warn'}
            statusText={
              !validation.hasLines
                ? 'Добавьте хотя бы одно место'
                : validation.invalidLineCount > 0
                  ? `Дозаполнить ${validation.invalidLineCount} шт.`
                  : undefined
            }
            onEdit={readOnly ? undefined : () => goTo(3)}
          >
            <p className="text-txt-secondary">
              Всего: <span className="font-semibold text-txt">{lines.length}</span>
              {validation.invalidLineCount > 0
                ? ` · дозаполнить ${validation.invalidLineCount}`
                : lines.length > 0
                  ? ' · все заполнены'
                  : ''}
            </p>
          </ReviewSection>

          <Card header="Отправка">
            <LabeledCheckbox
              checked={pdConsent}
              disabled={readOnly}
              onCheckedChange={changePdConsent}
              align="start"
            >
              Согласен на обработку персональных данных (обязательно для подачи)
            </LabeledCheckbox>

            {submitError ? (
              <Alert variant="danger" className="mt-4">
                {submitError}
              </Alert>
            ) : null}
            {fieldErrors.length > 0 ? (
              <ul className="ml-4 mt-2 list-disc text-13 text-red">
                {fieldErrors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            ) : null}

            {!readOnly ? (
              <div className="mt-4 flex flex-wrap gap-2.5">
                <Button disabled={submitting || !validation.ok} onClick={() => void submit()}>
                  {submitting ? 'Отправка…' : 'Отправить'}
                </Button>
              </div>
            ) : null}
          </Card>
        </div>
      ) : null}

      {/* Навигация шагов */}
      <div className="mt-6 flex items-center justify-between gap-3">
        <Button variant="secondary" disabled={step === 0} onClick={back}>
          Назад
        </Button>
        <div className="flex items-center gap-3">
          {!readOnly && step < STEPS.length - 1 && !stepValid[step] ? (
            <span className="text-12 text-red">На шаге есть незаполненные поля</span>
          ) : null}
          {step < STEPS.length - 1 ? (
            <Button disabled={!readOnly && !stepValid[step]} onClick={next}>
              Далее
            </Button>
          ) : null}
        </div>
      </div>

      {/* «+ Добавить сотрудника» шагов «Комиссия» (D3/D4) и «Ответственный» (MR !11):
          переиспользуем существующую модалку «Добавить в структуру» (вкладка «Добавить
          сотрудника»). По успеху — автоселект созданного сотрудника в источнике открытия
          (строка комиссии или поле ответственного, см. addTarget); закрытие — самой модалкой. */}
      {addTarget && !readOnly ? (
        <AddToStructureModal
          api={companyApi}
          initialTab="employee"
          onClose={() => setAddTarget(null)}
          onSuccess={(info) => {
            if (info.tab === 'employee') onEmployeeCreated(info.employee, addTarget)
            setAddTarget(null)
          }}
        />
      ) : null}
    </div>
  )
}

/**
 * Секция эхо-сводки шага «Проверка» (IC-3, п.6): заголовок с необязательным индикатором
 * статуса (ok/warn) и кнопкой «Изменить» (переход к соответствующему шагу), тело — таблица
 * значений либо произвольный контент.
 */
function ReviewSection({
  title,
  status,
  statusText,
  onEdit,
  children,
}: {
  title: string
  status?: 'ok' | 'warn'
  statusText?: string
  onEdit?: () => void
  children: React.ReactNode
}) {
  return (
    <Card
      header={
        <div className="flex items-center justify-between gap-3">
          <span className="flex items-center gap-2">
            {status ? (
              <CheckCircle2
                className={
                  status === 'ok'
                    ? 'size-4 shrink-0 stroke-current text-green'
                    : 'size-4 shrink-0 stroke-current text-red'
                }
                aria-hidden
              />
            ) : null}
            {title}
            {status === 'warn' && statusText ? (
              <span className="text-13 font-normal text-red">— {statusText}</span>
            ) : null}
          </span>
          {onEdit ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={onEdit}
              leftIcon={<Pencil className="size-4 stroke-current" aria-hidden />}
            >
              Изменить
            </Button>
          ) : null}
        </div>
      }
    >
      {children}
    </Card>
  )
}

/** Таблица «поле → значение» эхо-сводки; пустые значения помечаются «не заполнено». */
function ReviewTable({ rows }: { rows: Array<[string, string]> }) {
  return (
    <table className="w-full text-14">
      <tbody>
        {rows.map(([label, value]) => (
          <tr key={label} className="border-b border-line last:border-b-0">
            <td className="py-1.5 pr-3 align-top text-txt-secondary">{label}</td>
            <td className="py-1.5 align-top text-txt">
              {value.trim() ? value : <span className="text-txt-tertiary">не заполнено</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
